// @ts-check
// Dados de apoio das telas dos bots (contrato de bots, seção 22.2): departamentos, números de WhatsApp, membros e os
// outros bots, carregados uma vez por tela. Com eles saem os nomes do resumo de cada bloco, as escolhas dos formulários
// e o contexto das pendências da tela. Falha ao carregar um deles não derruba a tela: o resumo cai no texto genérico e
// a regra daquele dado fica para o servidor.
import { useEffect, useMemo, useState } from 'preact/hooks';
import { listChannels } from '../../data/conversations.js';
import { findManyRecords } from '../../data/rest.js';
import { listBookingPages, listBots } from '../../data/bots.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { indexBotTree } from '../../lib/bot-tree.js';
import { cardObject, memberDisplayName, stageOptions } from '../flows/flow-lookups.js';
import { translateMetadataLabel, t } from '../../i18n/index.js';
import { describeBotNode } from './bot-ui.js';

/**
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../../shared/bots/bot-summary.js').BotSummaryLookups} BotSummaryLookups
 * @typedef {import('../../lib/bot-list.js').BotSummary} BotSummary
 * @typedef {{ id: string, name: string, teamKind: string | null }} BotTeam
 * @typedef {{ id: string, label: string }} BotChannel
 * @typedef {import('../../data/bots.js').BookingPageChoice} BookingPageChoice
 * @typedef {{ teams: BotTeam[] | null, channels: BotChannel[] | null, bots: BotSummary[] | null, bookingPages: BookingPageChoice[] | null }} BotLookupData
 */

export const SERVICE_TEAM_OBJECT_NAME = 'serviceTeam';
const RESTRICTED_TEAM_KINDS = new Set(['OMBUDSMAN', 'PROTECTION']);

/** Departamentos que podem receber conversa (Ouvidoria e Proteção nunca são destino). @param {BotTeam[] | null} teams */
export const handoffTeams = (teams) => (teams ?? []).filter((team) => !RESTRICTED_TEAM_KINDS.has(team.teamKind ?? ''));

/** Rótulo do número: o telefone de exibição ou o nome. @param {any} channel */
const channelLabelOf = (channel) => channel?.settings?.displayPhoneNumber || channel?.displayName || channel?.handle || channel?.id;

/**
 * Carrega os dados de apoio (cada um por conta própria).
 * @returns {BotLookupData & { reload: () => void }}
 */
export function useBotLookupData() {
  const [data, setData] = useState(/** @type {BotLookupData} */ ({ teams: null, channels: null, bots: null, bookingPages: null }));
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let isCancelled = false;
    const merge = (/** @type {Partial<BotLookupData>} */ patch) => {
      if (!isCancelled) setData((current) => ({ ...current, ...patch }));
    };
    loadWorkspaceMembers();
    const teamObject = objectsByNameSingular.value.get(SERVICE_TEAM_OBJECT_NAME);
    if (teamObject) {
      findManyRecords(teamObject, { limit: 100, depth: 0 })
        .then((result) => merge({ teams: result.records.filter((record) => record.isActive !== false).map((record) => ({ id: record.id, name: record.name, teamKind: record.teamKind ?? null })) }))
        .catch(() => merge({ teams: [] }));
    } else merge({ teams: [] });
    listChannels().then((result) => merge({
      channels: result.channels.filter((channel) => channel.type === 'WHATSAPP').map((channel) => ({ id: channel.id, label: String(channelLabelOf(channel)) })),
    })).catch(() => merge({ channels: [] }));
    listBots().then((bots) => merge({ bots })).catch(() => merge({ bots: [] }));
    listBookingPages().then((bookingPages) => merge({ bookingPages })).catch(() => merge({ bookingPages: [] }));
    return () => {
      isCancelled = true;
    };
  }, [version]);
  return { ...data, reload: () => setVersion((value) => value + 1) };
}

/**
 * Campos de um alvo do CRM para "Gravar em campo" e "Alterar campo" (card, família, pessoa ou atendimento).
 * @param {string} target
 */
export function targetObject(target) {
  const name = target === 'CARD' ? 'enrollmentOpportunity' : target === 'HOUSEHOLD' ? 'household' : target === 'TICKET' ? 'serviceTicket' : 'person';
  return target === 'CARD' ? cardObject() : objectsByNameSingular.value.get(name) ?? null;
}

/**
 * Consultas do resumo de cada bloco (departamento, membro, outro bot, bloco de destino, etapa e campo).
 * @param {BotLookupData} data @param {BotDefinition | null} definition
 * @returns {BotSummaryLookups}
 */
export function buildBotSummaryLookups(data, definition) {
  const teamById = new Map((data.teams ?? []).map((team) => [team.id, team]));
  const botById = new Map((data.bots ?? []).map((bot) => [bot.id, bot]));
  const bookingPageById = new Map((data.bookingPages ?? []).map((page) => [page.id, page]));
  const members = workspaceMembersState.value ?? [];
  const index = definition ? indexBotTree(definition) : new Map();
  /** @type {BotSummaryLookups} */
  const lookups = {
    teamName: (id) => teamById.get(id)?.name ?? null,
    memberName: (id) => memberDisplayName(members.find((member) => member.id === id)) || null,
    botName: (id) => botById.get(id)?.name ?? null,
    bookingPage: data.bookingPages ? (id) => bookingPageById.get(id) ?? null : undefined,
    stageLabel: (value) => stageOptions().find((option) => option.value === value)?.label ?? null,
    fieldLabel: (target, fieldName) => {
      const field = targetObject(target)?.fields?.find((/** @type {any} */ candidate) => candidate.name === fieldName);
      return field ? translateMetadataLabel(field.label) : null;
    },
    nodeTitle: (nodeId) => {
      const node = index.get(nodeId)?.node;
      return node ? describeBotNode(node, { ...lookups, nodeTitle: () => null }).title : null;
    },
  };
  return lookups;
}

/** @param {BotLookupData} data @param {BotDefinition | null} definition */
export function useBotSummaryLookups(data, definition) {
  const members = workspaceMembersState.value;
  return useMemo(() => buildBotSummaryLookups(data, definition), [data.teams, data.bots, data.bookingPages, members, definition]);
}

/**
 * Contexto das pendências da tela com o que já está carregado.
 * @param {BotLookupData} data @param {{ botId: string, purpose: 'CAPTURE' | 'SERVICE' | 'ANY' }} options
 * @returns {import('../../lib/bot-validation.js').BotValidationContext}
 */
export function buildBotValidationContext(data, options) {
  const members = workspaceMembersState.value;
  return {
    purpose: options.purpose,
    botId: options.botId,
    teams: data.teams ? new Map(data.teams.map((team) => [team.id, { name: team.name, teamKind: team.teamKind }])) : null,
    channelIds: data.channels ? new Set(data.channels.map((channel) => channel.id)) : null,
    memberIds: members ? new Set(members.map((member) => member.id)) : null,
    bots: data.bots ? new Map(data.bots.map((bot) => [bot.id, { name: bot.name, purpose: bot.purpose }])) : null,
    bookingPages: data.bookingPages ? new Map(data.bookingPages.map((page) => [page.id, { name: page.name, isActive: page.isActive }])) : null,
    stageValues: cardObject() ? new Set(stageOptions().map((option) => option.value)) : null,
  };
}

/** Rótulo de um número pelo id (o id quando o número não está carregado). @param {BotLookupData} data @param {string} id */
export const channelLabel = (data, id) => data.channels?.find((channel) => channel.id === id)?.label ?? t('bots.triggers.unknownChannel');

/** Membros para os seletores. */
export function memberChoices() {
  return (workspaceMembersState.value ?? []).map((member) => ({ id: member.id, label: memberDisplayName(member) }));
}
